/**
 * Compact presentation helpers for the course list.
 *
 * The courses table showed each course's full program names joined with commas
 * — "Hospitality Operations Diploma, Global Management in Hospitality Diploma"
 * — which wrapped onto two lines and stretched every row. Programs already
 * carry an Abbreviation field (set in Manage Programs), so the list shows those
 * instead and keeps the full names in a tooltip.
 */

/** Derive an abbreviation from a program name when none is configured. */
export function inferProgramAbbreviation(name) {
  const text = String(name || '').trim();
  if (!text) return '';
  const words = text
    .split(/[\s—–-]+/)
    .map((word) => word.replace(/[^A-Za-z0-9]/g, ''))
    .filter(Boolean)
    // Joining words carry no signal in an acronym.
    .filter((word) => !['in', 'of', 'and', 'the', 'for', 'a', 'an'].includes(word.toLowerCase()));
  if (!words.length) return '';
  return words
    .map((word) => word[0].toUpperCase())
    .join('')
    .slice(0, 6);
}

/**
 * Build a lookup from program name to its display abbreviation.
 * Configured abbreviations win; anything missing falls back to an inferred one
 * so the column never renders a blank badge.
 */
export function buildProgramAbbreviationMap(programs) {
  const map = new Map();
  (programs || []).forEach((program) => {
    const name = String(program?.name || '').trim();
    if (!name) return;
    const configured = String(program?.abbreviation || '').trim().toUpperCase();
    map.set(name, configured || inferProgramAbbreviation(name));
  });
  return map;
}

/**
 * Resolve a course's programs into short badge labels.
 * Returns `{ badges, overflow, title }` — `badges` is capped at `limit`,
 * `overflow` is how many were hidden, and `title` is the full list for a
 * tooltip so nothing is actually lost.
 */
export function summarizeCoursePrograms(programNames, abbreviationMap, limit = 2) {
  const names = (programNames || []).map((n) => String(n || '').trim()).filter(Boolean);
  if (!names.length) return { badges: [], overflow: 0, title: '' };

  const labels = names.map((name) => ({
    name,
    label: abbreviationMap?.get(name) || inferProgramAbbreviation(name) || name,
  }));

  return {
    badges: labels.slice(0, limit),
    overflow: Math.max(0, labels.length - limit),
    title: names.join('\n'),
  };
}

/** "3 cr · 64 h", skipping whichever value is missing. */
export function formatCreditsAndHours(credits, hours) {
  const parts = [];
  const creditValue = Number(credits);
  const hourValue = Number(hours);
  if (Number.isFinite(creditValue) && creditValue > 0) parts.push(`${creditValue} cr`);
  if (Number.isFinite(hourValue) && hourValue > 0) parts.push(`${hourValue} h`);
  return parts.join(' · ');
}

/** The grey subtitle under a course title in the list. */
export function courseSubtitle(course) {
  const parts = [];
  if (course?.course_code) parts.push(course.course_code);
  const lessons = Number(course?.lessons_count || 0);
  parts.push(`${lessons} lesson${lessons === 1 ? '' : 's'}`);
  const load = formatCreditsAndHours(course?.credits, course?.hours);
  if (load) parts.push(load);
  return parts.join(' · ');
}
